import { EmailIcon } from '@/components/icons/email-icon';
import { FacebookIcon } from '@/components/icons/facebook-icon';
import { InstagramIcon } from '@/components/icons/instagram-icon';
import { XdotcomIcon } from '@/components/icons/xdotcom-icon';
import { SocialSharePlatform } from '@cinemark/content-api-client';

type PlatformComponentProps = {
  platform: SocialSharePlatform;
  shareMessage: string;
  shareSource?: string;
};

export const PlatformComponent = ({ platform, shareMessage, shareSource }: PlatformComponentProps): React.JSX.Element | null => {
  const socialIconsClassNames = 'icon icon-dark icon-social-share';

  const getShareUrl = (): string => {
    const messageToUse = platform.customMessage || shareMessage;
    const url = shareSource || window.location.href;
    switch (platform.platform) {
      case 'twitter':
        return `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(messageToUse)}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(messageToUse)}`;
      case 'instagram':
        return 'https://www.instagram.com/';
      case 'email':
        return `mailto:?subject=${encodeURIComponent(platform.emailSubject || 'Share')}&body=${encodeURIComponent(messageToUse)}`;
      default:
        return '';
    }
  };

  const getIcon = (): React.ReactElement | null => {
    switch (platform.platform) {
      case 'twitter':
        return (
          <XdotcomIcon aria-label="X icon"
            className={socialIconsClassNames}
            role="img"
          />
        );
      case 'facebook':
        return (
          <FacebookIcon aria-label="Facebook icon"
            className={socialIconsClassNames}
            role="img"
          />
        );
      case 'instagram':
        return (
          <InstagramIcon aria-label="Instagram icon"
            className={socialIconsClassNames}
            role="img"
          />
        );
      case 'email':
        return (
          <EmailIcon aria-label="Email icon"
            className={socialIconsClassNames}
            role="img"
          />
        );
      default:
        return null;
    }
  };

  const icon = getIcon();
  const url = getShareUrl();

  if (!icon || !url) return null;

  return (
    <a aria-label={`Share on ${platform.platform}`}
      className="text-gray-600 hover:text-gray-800"
      data-test={`social-share-${platform.platform}`}
      href={url}
      rel="noopener noreferrer"
      target="_blank"
    >
      {icon}
    </a>
  );
};
